import './alerts.scss'
import templateUrl from './alerts.html'

// Milliseconds between alerts fetch
const defaultAlertsInterval = 600000 // 10 minutes

/**
 * Shows user notifications and warnings at the top of the page.
 * Notifications are fetched from server every 10 minutes but can be added
 * client-side with an event hook (see examples).
 *
 * @example
 * <tmr-alerts></tmr-alerts>
 *
 * @example <caption>Client-side notifications in a run function</caption>
 * import myAlertTemplate from './myAlertTemplate.html';
 *
 * let MyCustomRun = ($rootScope, Alert) => { 'ngInject';
 *   $rootScope.$on('alertsloaded', (event, alerts) => {
 *     let alert = Alert.create({
 *       id: 'some-unique-id',
 *       persistent: true, // Persistent alerts are shown again after page reload
 *       message: JSON.stringify({ en: 'hello world', it: 'ciao mondo' })
 *     });
 *     let alertWithTemplate = Alert.create({
 *       id: 'some-unique-id-2',
 *       template: myAlertTemplate // Use template instead of message to add dynamic behaviour (buttons, etc)
 *     });
 *     alerts.push(alert, alertWithTemplate);
 *   });
 * }
 * export { MyCustomRun as run };
 *
 * @type {ngComponent}
 */
export const tmrAlerts = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrAlertsController',
}

/**
 * Controller of component {@link tmrAlerts}.
 *
 * @type {ngController}
 */
export class tmrAlertsController {
  static ngType = 'controller'

  constructor(
    $interval,
    $rootScope,
    TmrLocalStorage,
    UserStorage,
    Alert,
    $filter
  ) {
    'ngInject'
    Object.assign(this, {
      $interval,
      $rootScope,
      TmrLocalStorage,
      UserStorage,
      Alert,
      $filter,
    })
  }

  $onInit() {
    this.tmpDismissedAlerts = [] // These will show again when the page is reloaded
    this.alerts = []
    this.colors = {
      foreground: { NOTICE: 'accent', WARNING: 'background' },
      background: { NOTICE: 'background', WARNING: 'temera-red-900' },
    }
    this.loadAlerts()
    this.currentAlertsInterval = defaultAlertsInterval
    this.deregisterOnLogout = this.$rootScope.$on('logout', () =>
      this.onLogout()
    )
    this.deregisterOnLoggedUserUpdate = this.$rootScope.$on(
      'loggeduserupdate',
      () => this.onUserChange()
    )
    this.currentAlertsPromise = this.$interval(
      () => this.loadAlerts(),
      this.currentAlertsInterval
    )
  }

  $onDestroy() {
    this.deregisterOnLogout()
    this.deregisterOnLoggedUserUpdate()
    this.$interval.cancel(this.currentAlertsPromise)
  }

  onLogout() {
    this.alerts.length = 0
  }

  onUserChange() {
    this.alerts.length = 0
    this.loadAlerts()
  }

  loadAlerts() {
    this.UserStorage.getUser().then(() => {
      if (this.UserStorage.isAuthenticated())
        return this.Alert.current().$promise.then(alerts =>
          this.onAlertsLoaded(alerts)
        )
    })
  }

  /** Prepares undismissed alerts for render */
  onAlertsLoaded(alerts) {
    // Give opportunity to alter the alerts list
    this.$rootScope.$emit('alertsloaded', alerts)

    // Dismissed alerts in local storage are grouped by user
    let user = this.UserStorage.getUserSync()
    let dismissedAlerts =
      this.TmrLocalStorage.get('dismissedAlerts', {})[user.id] ?? []
    this.alerts.length = 0
    this.alerts.push(
      ...alerts.filter(
        ({ id, persistent }) =>
          !(persistent ? this.tmpDismissedAlerts : dismissedAlerts).includes(id)
      )
    )
  }

  dismiss({ id, persistent }) {
    if (persistent) {
      this.tmpDismissedAlerts.push(id)
    } else {
      let user = this.UserStorage.getUserSync()
      let dismissedAlerts = this.TmrLocalStorage.get('dismissedAlerts', {})
      dismissedAlerts[user.id] = dismissedAlerts[user.id] ?? []
      dismissedAlerts[user.id].push(id)
      this.TmrLocalStorage.set('dismissedAlerts', dismissedAlerts)
    }
    this.alerts.splice(
      this.alerts.findIndex(alert => id == alert.id),
      1
    )
  }

  formatEventDate(timestamp) {
    // If time is 00:00:00 or 23:59:59, we assume that no time was entered, and show only the date
    const date = new Date(timestamp)
    const time = date.toTimeString()
    return this.$filter('date')(
      timestamp,
      time.startsWith('00:00:00') || time.startsWith('23:59:59')
        ? 'shortDate'
        : 'short'
    )
  }
}
